// Library Methods
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useState } from "react";

// Components
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Alert from "src/components/shared/alerts/Alert/Alert";
import DefaultModal from "src/components/shared/modals/DefaultModal/DefaultModal";

// Styles
import {
  ModalCustomCancel,
  ModalCustomDeleteSolid,
} from "src/styles/mui/muiStylesHelper";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

// Services
import { getExamsForDryEyeProfiles } from "src/services/clinics/api";

// Types
import { DryEyeProfileType } from "src/services/clinics/types";

// Hooks
import useTokenAccess from "src/hooks/auth/useTokenAccess";

type DeleteDryEyeModalType = {
  open: boolean;
  isLoading: boolean;
  selectedProfile: DryEyeProfileType | undefined;
  onClose: () => void;
  onConfirm: () => void;
};

const DeleteDryEyeModal = ({
  open,
  isLoading,
  selectedProfile,
  onClose,
  onConfirm,
}: DeleteDryEyeModalType) => {
  const { t } = useTranslation();
  const { clinicInfo } = useClinicContext();
  const { getToken } = useTokenAccess();

  const [linkedExamCount, setLinkedExamCount] = useState(0);

  // refetch the related exams every time the clinic or selected lens change
  const fetchLinkedExams = useCallback(async () => {
    if (!selectedProfile) return;

    const token = await getToken();
    getExamsForDryEyeProfiles(token, clinicInfo?.id, selectedProfile?.id).then(
      (res) => {
        setLinkedExamCount(res?.length);
      }
    );
  }, [clinicInfo?.id, selectedProfile, getToken]);

  useEffect(() => {
    if (open && selectedProfile) {
      fetchLinkedExams();
    }
  }, [fetchLinkedExams, open, selectedProfile]);

  return (
    <DefaultModal
      isOpen={open}
      onClose={onClose}
      title={t("Delete Dry Eye Option?")}
      maxWidth="xs"
      modalActions={
        <Grid
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "8px",
          }}
        >
          <Button sx={ModalCustomCancel} onClick={onClose} disabled={isLoading}>
            {t("Cancel")}
          </Button>
          <Button
            sx={ModalCustomDeleteSolid}
            onClick={onConfirm}
            disabled={isLoading}
          >
            {t("Delete")}
          </Button>
        </Grid>
      }
    >
      <Grid p="16px" display="flex" flexDirection="column" gap="20px">
        {linkedExamCount > 0 && (
          <Alert
            variant="warning"
            alertContent={t(
              "This lens is linked to one or more exams that haven't been started. Deleting it will also delete the exams."
            )}
          />
        )}
        <Typography fontSize="14px">
          {t("Are you sure you want to delete '{{ lensOptionName }}'?", {
            lensOptionName: selectedProfile?.name ?? "",
          })}
        </Typography>
      </Grid>
    </DefaultModal>
  );
};

export default DeleteDryEyeModal;
